// Icons

import ChannelsClassicIcon from '@components/atoms/icons/channelsClassicIcon';
import ChannelsGuideIcon from '@components/atoms/icons/channelsGuideIcon';
import ChannelsGridIcon from '@components/atoms/icons/channelsGridIcon';
import ChannelsLineupIcon from '@components/atoms/icons/channelsLineupIcon';
import InformationIcon from '@components/atoms/icons/information';
import LanguageIcon from '@components/atoms/icons/language';
import L7DIcon from '@components/atoms/icons/l7d';
import PlayIcon from '@components/atoms/icons/play';
import RecordCancelIcon from '@components/atoms/icons/recordCancel';
import RecordDeleteIcon from '@components/atoms/icons/recordDelete';
import RecordStartIcon from '@components/atoms/icons/recordStart';
import RestartIcon from '@components/atoms/icons/restart';
import StopWatchingIcon from '@components/atoms/icons/stopWatching';
import WatchEverythingIcon from '@components/atoms/icons/watchEverything';
import WatchLaterIcon from '@components/atoms/icons/watchLater';
// ---

import AdUtils from '@shared/utils/ads';
import AnalyticsInstance, { AnalyticEventType } from './analytics';
import ChannelUtils from '@shared/utils/channels';
import ChannelView from '@shared/types/enums/channelView';
import ContainerType from '@shared/types/enums/container';
import Content from '@shared/types/enums/content';
import DeviceConfig from '@devices/config';
import Event from '@shared/classes/event';
import FastEvent from '@shared/classes/fastEvent';
import Features from '@shared/types/enums/features';
import FlavourConfig from '@shared/config/flavours';
import MediaProduction from '@shared/classes/mediaProduction';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import Plan from '@shared/classes/plan';
import PlanUtils from '@shared/utils/plan';
import { PlayerType } from '@shared/types/enums/player';
import Recording from '@shared/types/enums/recording';
import RecordType from '@shared/types/enums/recordType';
import Routes from '@shared/types/enums/routes';
import Sections from '@shared/types/enums/sections';
import Services from '@shared/types/enums/services';
import Storage from '@storage';
import addKeepWatching from '@shared/api/keepWatching/addKeepWatchingElement';
import goToChannelsGuide from '@shared/actions/channels/goToChannelsGuide';
import goToChannelsGrid from '@shared/actions/channels/goToChannelsGrid';
import goToChannelsLineup from '@shared/actions/channels/goToChannelsLineup';
import goToChannelsClassic from '@shared/actions/channels/goToChannelsClassic';
import goToContent from '@shared/actions/content/goToContent';
import goToDetail from '@shared/actions/detail/goToDetail';
import goToWatchEverything from '@shared/actions/routes/goToWatchEverything';
import goToL7D from '@shared/actions/routes/goToL7D';
import removeKeepWatching from '@shared/api/keepWatching/removeKeepWatchingElement';
import { FloatingMenuButton } from '@shared/classes/floatingMenu';
import { RailType } from '@shared/types/enums/rail';
import { addRecordingRequest } from '@shared/api/recording/recordingsRequest';
import { clearPauseAd, setVideoAd } from '@shared/store/slices/ads.slice';
import { closeFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { play, setPosition } from '@shared/store/slices/media.slice';
import { reload } from '@shared/store/slices/floatingMenu.slice';
import { setCurrentRecording } from '@shared/store/slices/recordings.slice';
import { setParams, updateParams } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import {
  clearModal,
  setModal,
  setModalParams,
} from '@shared/store/slices/modal.slice';
import {
  addKeepWatchingItem,
  removeKeepWatchingItem,
} from '@shared/store/slices/keepWatching.slice';

const LOG_TAG = '[FloatingMenu]';

/**
 * Logs a floating menu message with a common prefix.
 */
const log = (...args: unknown[]) => {
  console.log(LOG_TAG, ...args);
};

/**
 * Returns a compact summary of an asset for logging purposes.
 *
 * @param {Event | null | undefined} asset - Asset to describe
 */
const describeAsset = (asset?: Event | null) => {
  if (!asset) return asset;
  return {
    eventId: asset.eventId,
    type: asset.type,
    channelId: asset.channel?.id,
    mocked: asset.mocked,
    rail: asset.rail,
  };
};

/**
 * Utility object containing common actions for floating menu operations.
 */
const Actions = {
  closeMenu: () => {
    log('closeMenu');
    store.dispatch(closeFloatingMenu());
  },
  reloadMenu: () => {
    log('reloadMenu');
    store.dispatch(reload());
  },
  triggerAd: (section?: Sections) => {
    if (section) {
      const adSource = AdUtils.getSource(section);
      if (adSource) {
        store.dispatch(setVideoAd(adSource));
        return true;
      }
      log('triggerAd: no ad source for section', section);
    } else {
      log('triggerAd: no section provided');
    }
    return false;
  },
};

/**
 * Collection of predefined floating menu button configurations.
 * Each button defines an action, icon, and text label for menu interactions.
 */
const Buttons = {
  /**
   * Quick access buttons
   */
  CHANNELS_CLASSIC: new FloatingMenuButton({
    text: 'ACTION.VIEWS.CLASSIC_VIEW',
    action: (asset?: Event | null) => {
      log('CLASSIC_CHANNEL action', { asset: describeAsset(asset) });
      if (asset) {
        Actions.closeMenu();
        Storage.set('userChannelViewSelection', `${ChannelView.Classic}`);
        goToChannelsClassic({ channel: asset.channel });
      }
    },
    icon: ChannelsClassicIcon,
  }),
  CHANNELS_GRID: new FloatingMenuButton({
    text: 'ACTION.VIEWS.GRID_VIEW',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        Storage.set('userChannelViewSelection', `${ChannelView.Grid}`);
        goToChannelsGrid({ channel: asset.channel });
      }
    },
    icon: ChannelsGridIcon,
  }),
  CHANNELS_LINEUP: new FloatingMenuButton({
    text: 'ACTION.VIEWS.LINEUP_VIEW',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        Storage.set('userChannelViewSelection', `${ChannelView.Lineup}`);
        goToChannelsLineup({ channel: asset.channel, stacked: false });
      }
    },
    icon: ChannelsLineupIcon,
  }),
  DETAIL: new FloatingMenuButton({
    text: 'ACTION.DETAIL',
    action: (asset?: Event | null) => {
      log('DETAIL action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      if (asset) {
        goToDetail(asset as FastEvent);
      } else {
        log('DETAIL action: skipped, no asset');
      }
    },
    icon: InformationIcon,
  }),
  GUIDE: new FloatingMenuButton({
    text: 'ACTION.GUIDE',
    action: (asset?: Event | null) => {
      log('GUIDE action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      Actions.triggerAd(Sections.GUIDE_TV);
      goToChannelsGuide(asset);
    },
    icon: ChannelsGuideIcon,
  }),
  LAST7DAYS: new FloatingMenuButton({
    text: FlavourConfig.capabilities.u7d ? 'ACTION.U7D' : 'ACTION.WATCH_MORE',
    action: (asset?: Event | null) => {
      log('LAST7DAYS action', { asset: describeAsset(asset) });
      // Get ad depending on the page the user triggers the menu from
      const { router } = store.getState();
      let section = undefined;
      switch (router.route.Page) {
        case Pages.CHANNELS_LINEUP:
        case Pages.INITIAL:
        case Pages.RECORDINGS:
          section = Sections.CHANNELS_U7D;
          break;
        case Pages.MOVIES:
          section = Sections.MOVIES;
          break;
        case Pages.SERIES:
          section = Sections.SERIES;
          break;
        case Pages.SHOWS:
          section = Sections.SHOWS;
          break;
      }
      log('LAST7DAYS action: resolved ad section', {
        page: router.route.Page,
        section,
      });

      // Route to new page and dispatch ads
      Actions.closeMenu();
      Actions.triggerAd(section);
      goToL7D(asset);
    },
    icon: FlavourConfig.capabilities.u7d ? L7DIcon : WatchEverythingIcon,
  }),
  DIAL: new FloatingMenuButton({
    text: 'ACTION.CHANNELS',
    action: (asset?: Event | null) => {
      log('TV_CHANNELS action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      const { router } = store.getState();
      if (router.route.Page === Pages.CHANNELS_CLASSIC) {
        Storage.set('userChannelViewSelection', `${ChannelView.Lineup}`);
        goToChannelsLineup({ channel: asset?.channel });
      } else {
        goToChannelsLineup();
      }
      store.dispatch(
        setVideoAd(
          AdUtils.getSource(Sections.CHANNELS_MAIN, false, asset?.channel)
        )
      );
    },
    icon: ChannelsLineupIcon,
  }),
  /**
   * Player buttons
   */
  PLAY: new FloatingMenuButton({
    text: 'ACTION.PLAY',
    action: (asset?: Event | null) => {
      log('PLAY action', { asset: describeAsset(asset) });
      if (asset) {
        Actions.closeMenu();
        goToContent(asset, false, asset.mocked);
      } else {
        log('PLAY action: skipped, no asset');
      }
    },
    icon: PlayIcon,
  }),
  PLAY_LIVE: new FloatingMenuButton({
    text: 'ACTION.WATCH_NOW',
    action: (asset?: Event | null) => {
      log('PLAY_LIVE action', { asset: describeAsset(asset) });
      if (asset) {
        Actions.closeMenu();
        goToContent(asset, false, asset.mocked);
      } else {
        log('PLAY_LIVE action: skipped, no asset');
      }
    },
    icon: PlayIcon,
  }),
  LANGUAGES: new FloatingMenuButton({
    text: 'ACTION.LANGUAGES',
    action: (_asset?: Event | null, node?: number | null) => {
      log('LANGUAGES action', { asset: describeAsset(_asset), node: node });
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node, event: _asset }));
      store.dispatch(setModal(Modals.PLAYER_LANGUAGE));
    },
    icon: LanguageIcon,
  }),
  /**
   * Record buttons
   */
  RECORD: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      log('RECORD action', { asset: describeAsset(asset), node: node });
      if (asset && !asset.serieId) {
        log('RECORD action: requesting show recording', asset.eventId);
        addRecordingRequest(asset.eventId, RecordType.SHOW).then(() => {
          log('RECORD action: recording request done', asset.eventId);
          store.dispatch(reload());
        });
        Actions.closeMenu();
        return;
      }
      log('RECORD action: serie asset, opening recording request modal');
      Actions.closeMenu();
      store.dispatch(setModalParams({ asset, previousNode: node }));
      store.dispatch(setModal(Modals.RECORDING_REQUEST));
    },
    icon: RecordStartIcon,
  }),
  RECORD_UPGRADE_PLAN: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      log('RECORD_UPGRADE_PLAN action', {
        asset: describeAsset(asset),
        node: node,
      });
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getNextUpgradablePlan(
        asset?.channel,
        true
      );
      log('RECORD_UPGRADE_PLAN action: upgradable plan', upgradablePlan?.id);
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: RecordStartIcon,
  }),
  RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      log('RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN action', {
        asset: describeAsset(asset),
        node: node,
      });
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getUpgradablePlan(
        asset?.channel,
        true
      );
      log(
        'RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN action: upgradable plan',
        upgradablePlan?.id
      );
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: RecordStartIcon,
  }),
  CANCEL_RECORD: new FloatingMenuButton({
    text: 'ACTION.CANCEL_RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      log('CANCEL_RECORD action', { asset: describeAsset(asset), node: node });
      if (asset) {
        const { recordings } = store.getState();
        const identifier = recordings.identifiers.find((item) => {
          return item.code === asset?.eventId;
        });
        log('CANCEL_RECORD action: recording identifier', identifier);
        store.dispatch(setCurrentRecording({ ...identifier }));
        Actions.closeMenu();
        store.dispatch(
          setModalParams({
            asset,
            previousNode: node,
            recordType: Recording.Pending,
          })
        );
        store.dispatch(setModal(Modals.RECORDING_DELETE));
      } else {
        log('CANCEL_RECORD action: skipped, no asset');
      }
    },
    icon: RecordCancelIcon,
  }),
  DELETE_RECORD: new FloatingMenuButton({
    text: 'ACTION.DELETE_RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      log('DELETE_RECORD action', { asset: describeAsset(asset), node: node });
      if (asset) {
        const { recordings } = store.getState();
        const identifier = recordings.identifiers.find((item) => {
          return item.code === asset?.eventId;
        });
        log('DELETE_RECORD action: recording identifier', identifier);
        store.dispatch(setCurrentRecording({ ...identifier }));
        Actions.closeMenu();
        store.dispatch(
          setModalParams({
            asset,
            previousNode: node,
            recordType: Recording.Finished,
          })
        );
        store.dispatch(setModal(Modals.RECORDING_DELETE));
      } else {
        log('DELETE_RECORD action: skipped, no asset');
      }
    },
    icon: RecordDeleteIcon,
  }),
  NOT_ALLOWED_RECORD_ANONYMOUS: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (_asset?: Event | null, node?: number | null) => {
      log('NOT_ALLOWED_RECORD_ANONYMOUS action', {
        asset: describeAsset(_asset),
        node: node,
      });
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node }));
      store.dispatch(setModal(Modals.ANONYMOUS_GENERIC));
    },
    icon: RecordStartIcon,
  }),
  /**
   * Replay buttons
   */
  RESTART_CONTENT: {
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      log('RESTART_CONTENT action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      goToContent(asset, true);
    },
    icon: RestartIcon,
  },
  RESTART_MEDIA: {
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      log('RESTART_MEDIA action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      store.dispatch(clearPauseAd());
      store.dispatch(
        setPosition({
          position: (asset as FastEvent).startBookmark,
          seek: true,
        })
      );
      setTimeout(() => {
        log('RESTART_MEDIA action: resuming playback');
        store.dispatch(play());
      });
    },
    icon: RestartIcon,
  },
  RESTART_PLAYER: new FloatingMenuButton({
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      log('RESTART_PLAYER action', { asset: describeAsset(asset) });
      Actions.closeMenu();
      store.dispatch(clearPauseAd());
      store.dispatch(
        setPosition({ position: (asset as FastEvent)?.start || 0, seek: true })
      );
    },
    icon: RestartIcon,
  }),
  STARTOVER: new FloatingMenuButton({
    text: 'ACTION.REPLAY',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    action: (_asset?: Event | null, node?: number | null) => {
      log('STARTOVER action', { asset: describeAsset(_asset), node: node });
      Actions.closeMenu();
      AnalyticsInstance.addEvent(AnalyticEventType.RESTART);
      Actions.triggerAd(Sections.RESTART_LIVE);
      store.dispatch(
        setParams({
          position: 0,
          restart: true,
        })
      );
      store.dispatch(clearModal());
      store.dispatch(clearPauseAd());
      setTimeout(() => {
        log('STARTOVER action: resuming playback');
        store.dispatch(play());
      }, 1000);
    },
    icon: RestartIcon,
  }),
  /**
   * Watch everything buttons
   */
  WATCH_EVERYTHING: new FloatingMenuButton({
    text: 'ACTION.SEE_ALL',
    action: (asset?: Event | null) => {
      log('WATCH_EVERYTHING action', { asset: describeAsset(asset) });
      if (asset) {
        Actions.closeMenu();
        goToWatchEverything(asset);
      } else {
        log('WATCH_EVERYTHING action: skipped, no asset');
      }
    },
    icon: WatchEverythingIcon,
  }),
  STOP_WATCHING: new FloatingMenuButton({
    text: 'ACTION.STOP_WATCHING',
    action: (asset?: Event | null) => {
      log('STOP_WATCHING action', { asset: describeAsset(asset) });
      if (asset?.eventId) {
        store.dispatch(removeKeepWatchingItem(asset.eventId));
        store.dispatch(updateParams({ updateKeepWatching: true }));
        Actions.closeMenu();
        removeKeepWatching(asset.eventId);
      } else {
        log('STOP_WATCHING action: skipped, no eventId');
      }
    },
    icon: StopWatchingIcon,
  }),
  /**
   * Watch later buttons
   */
  WATCH_LATER: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (asset?: Event | null) => {
      log('WATCH_LATER action', { asset: describeAsset(asset) });
      if (asset?.eventId) {
        store.dispatch(addKeepWatchingItem({ id: asset.eventId, mark: 0 }));
        Actions.closeMenu();
        addKeepWatching(asset.eventId);
      } else {
        log('WATCH_LATER action: skipped, no eventId');
      }
    },
    icon: WatchLaterIcon,
  }),
  WATCH_LATER_ANONYMOUS: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (_asset?: Event | null, node?: number | null) => {
      log('WATCH_LATER_ANONYMOUS action', {
        asset: describeAsset(_asset),
        node: node,
      });
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node }));
      store.dispatch(setModal(Modals.ANONYMOUS_GENERIC));
    },
    icon: WatchLaterIcon,
  }),
  WATCH_LATER_UPGRADABLE_PLAN: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (asset?: Event | null, node?: number | null) => {
      log('WATCH_LATER_UPGRADABLE_PLAN action', {
        asset: describeAsset(asset),
        node: node,
      });
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getUpgradablePlan(asset?.channel);
      log(
        'WATCH_LATER_UPGRADABLE_PLAN action: upgradable plan',
        upgradablePlan?.id
      );
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: WatchLaterIcon,
  }),
};

/**
 * Adds the classic channel view button to the menu if on the channels main page.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} _asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getClassicViewButton(
  _route: number,
  _asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  if (
    !router.route.stack.includes(Pages.ZAPPER) &&
    (router.route.Page === Pages.CHANNELS_LINEUP ||
      router.route.Page === Pages.CHANNELS_GRID)
  ) {
    buttons.push(Buttons.CHANNELS_CLASSIC);
  }
}

/**
 * Adds the lineup channel view button to the menu if on the channels grid or classic page.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} _asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getLineupViewButton(
  _route: number,
  _asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  if (
    router.route.Page === Pages.CHANNELS_GRID ||
    router.route.Page === Pages.CHANNELS_CLASSIC
  ) {
    buttons.push(Buttons.CHANNELS_LINEUP);
  }
}

/**
 * Adds the grid channel view button to the menu if on the channels lineup or classic page.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} _asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getGridViewButton(
  _route: number,
  _asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  if (
    !router.route.stack.includes(Pages.ZAPPER) &&
    (router.route.Page === Pages.CHANNELS_LINEUP ||
      router.route.Page === Pages.CHANNELS_CLASSIC)
  ) {
    buttons.push(Buttons.CHANNELS_GRID);
  }
}

/**
 * Adds the main action button (channels or play) based on route and asset.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getMainButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (route === Routes.ZAPPER) {
    const button = Buttons.DIAL;
    buttons.push(button);
  } else if (
    asset &&
    (route === Routes.MAIN ||
      route === Routes.DETAIL ||
      route === Routes.CHANNELS_LINEUP)
  ) {
    const upgradablePlan = ChannelUtils.getUpgradablePlan(asset.channel);
    if (upgradablePlan) {
      log('getMainButton: skipped, upgradable plan', upgradablePlan?.id);
      return;
    }
    if (
      asset.type === Content.EPG_EVENT &&
      !asset.isCurrentEvent() &&
      !asset.mocked
    ) {
      log('getMainButton: skipped, EPG event is not current');
      return;
    }
    if (
      (asset as MediaProduction).type === Content.MEDIA_PRODUCTION &&
      !(asset as MediaProduction).streams
    ) {
      log('getMainButton: skipped, media production without streams');
      return;
    }
    if (asset.type === Content.SERIES || asset.type === Content.SEASON) {
      log('getMainButton: skipped, series or season container');
      return;
    }
    if (asset.type === Content.EPG_EVENT) {
      log('getMainButton: adding PLAY_LIVE');
      buttons.push(Buttons.PLAY_LIVE);
    } else {
      log('getMainButton: adding PLAY');
      buttons.push(Buttons.PLAY);
    }
  } else {
    log('getMainButton: skipped', { route, hasAsset: !!asset });
  }
}

/**
 * Adds restart/replay button based on content type and playback state.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getRestartButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { media } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.LAST_SHOWS, [
    ...(asset?.channel?.extraPlans?.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  const channelServices = asset?.channel?.services || [];
  if (asset?.mocked) return;
  // Currently in main, channel list, detail or player, allow restart content or restart media
  if (
    asset &&
    (route === Routes.MAIN ||
      route === Routes.CHANNELS_LINEUP ||
      route === Routes.DETAIL ||
      route === Routes.PLAYER)
  ) {
    if (
      asset.type === Content.MEDIA_PRODUCTION ||
      asset.type === Content.SHOW
    ) {
      // Get keep watching value from state or event
      const { keepWatching } = store.getState();
      const keepWatchingValue =
        keepWatching.data.find((item) => {
          return item.id === asset.eventId;
        })?.mark || (asset as FastEvent).keepWatchingMark;

      // If keep watching value is found, add restart media button
      if (route !== Routes.PLAYER) {
        if (keepWatchingValue) {
          buttons.push(Buttons.RESTART_CONTENT);
        }
      } else {
        buttons.push(Buttons.RESTART_MEDIA);
      }
    } else if (asset.type === Content.EPG_EVENT) {
      if (
        asset.isCurrentEvent() &&
        channelServices?.includes(Services.TIMESHIFT) &&
        planToEvaluate
      ) {
        log('getRestartButton: adding RESTART_CONTENT (EPG timeshift)');
        buttons.push(Buttons.RESTART_CONTENT);
      } else {
        log('getRestartButton: skipped EPG event, not restartable');
      }
    }
  }
  // Currently in player, allow restart player
  else if (route === Routes.PLAYER && media.timeshift) {
    buttons.push(Buttons.RESTART_PLAYER);
  }
  // Currently in zapper, allow startover if timeshift is enabled and plan is evaluated
  else if (route === Routes.ZAPPER && media.timeshift) {
    if (planToEvaluate && channelServices?.includes(Services.TIMESHIFT)) {
      log('getRestartButton: adding STARTOVER');
      buttons.push(Buttons.STARTOVER);
    } else {
      log('getRestartButton: skipped STARTOVER, no plan or timeshift service');
    }
  } else {
    log('getRestartButton: no restart button for route', route);
  }
}

/**
 * Adds recording-related buttons based on channel services and plan features.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getRecordButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  const isSeasonRecorded =
    router?.params?.current?.isSeason && router?.params?.current?.isRecord;
  if (
    !asset?.channel?.services?.includes(Services.RECORDINGS) &&
    !asset?.channel?.services?.includes(Services.RECORDINGS_FREE) &&
    !isSeasonRecorded
  ) {
    log('getRecordButton: skipped, channel has no recording services');
    return;
  }
  if (route === Routes.PLAYER) {
    log('getRecordButton: skipped, player route');
    return;
  }
  const { user } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.ALLOW_RECORD, [
    ...(asset?.channel?.extraPlans.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  const upgradablePlan = ChannelUtils.getUpgradablePlan(asset?.channel);
  log('getRecordButton: evaluating', {
    isSeasonRecorded,
    hasAllowRecordPlan: !!planToEvaluate,
    upgradablePlan: upgradablePlan?.id,
    isAnonymous: user.isAnonymous,
  });

  // Nested functions

  /**
   * Checks if the current event is already being recorded.
   *
   * @returns {Object | undefined} Recording type if found, undefined otherwise
   */
  const checkRecording = () => {
    const { recordings } = store.getState();
    const recordingFound = recordings.identifiers.find((item) => {
      return item.code === asset?.eventId;
    });
    if (recordingFound) {
      const isPendingEvent = asset?.isFutureEvent() || asset?.isCurrentEvent();
      return { type: isPendingEvent ? Recording.Pending : Recording.Finished };
    }
  };

  /**
   * Determines and adds the appropriate record button based on recording state.
   */
  const getButton = () => {
    const check = checkRecording();
    log('getRecordButton: recording check', check);
    if (check?.type === Recording.Pending) {
      log('getRecordButton: adding CANCEL_RECORD');
      buttons.push(Buttons.CANCEL_RECORD);
    } else if (check?.type === Recording.Finished || isSeasonRecorded) {
      log('getRecordButton: adding DELETE_RECORD');
      buttons.push(Buttons.DELETE_RECORD);
    }
    if (!check) {
      if (asset?.mocked || asset?.type !== Content.EPG_EVENT) {
        log('getRecordButton: no record button, not a recordable event');
      } else if (upgradablePlan) {
        log('getRecordButton: adding RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN');
        buttons.push(Buttons.RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN);
      } else {
        log('getRecordButton: adding RECORD');
        buttons.push(Buttons.RECORD);
      }
    }
  };

  // Record button logic
  if (
    planToEvaluate &&
    !(asset?.rail && asset?.rail === RailType.KEEP_WATCHING)
  ) {
    getButton();
  } else if (asset?.type === Content.EPG_EVENT) {
    if (
      (asset?.channel?.services?.includes(Services.RECORDINGS) &&
        !(planToEvaluate as Plan)?.isFree) ||
      asset?.channel?.services?.includes(Services.RECORDINGS_FREE)
    ) {
      if (planToEvaluate) {
        getButton();
      } else {
        if (user.isAnonymous) {
          log('getRecordButton: adding NOT_ALLOWED_RECORD_ANONYMOUS');
          buttons.push(Buttons.NOT_ALLOWED_RECORD_ANONYMOUS);
        } else if (!planToEvaluate) {
          if (
            (asset?.channel?.services?.includes(Services.RECORDINGS) ||
              asset?.channel?.services?.includes(Services.RECORDINGS_FREE)) &&
            ChannelUtils.getNextUpgradablePlan(asset.channel, true)
          ) {
            log('getRecordButton: adding RECORD_UPGRADE_PLAN');
            buttons.push(Buttons.RECORD_UPGRADE_PLAN);
          } else {
            log('getRecordButton: no next upgradable plan');
          }
        }
      }
    } else if (asset?.channel?.services?.includes(Services.RECORDINGS)) {
      if (ChannelUtils.getNextUpgradablePlan(asset.channel, true)) {
        log('getRecordButton: adding RECORD_UPGRADE_PLAN (paid recordings)');
        buttons.push(Buttons.RECORD_UPGRADE_PLAN);
      } else {
        log('getRecordButton: no next upgradable plan (paid recordings)');
      }
    }
  } else {
    log('getRecordButton: no record button, not an EPG event');
  }
}

/**
 * Adds detail button if asset is available and not mocked.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getDetailButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (asset && !asset.mocked && route !== Routes.DETAIL) {
    log('getDetailButton: adding DETAIL');
    buttons.push(Buttons.DETAIL);
  } else {
    log('getDetailButton: skipped', { route, hasAsset: !!asset });
  }
}

/**
 * Adds language selection button if multiple audio/subtitle tracks are available.
 * Hidden for CBCS channels played through AVPlayer, since it does not support switching tracks.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getLanguageButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { media } = store.getState();
  const isCBCSWithAvPlayer =
    !!asset?.channel?.avplay &&
    DeviceConfig.defaultDashPlayer === PlayerType.AVPLAYER;
  if (
    [Routes.PLAYER, Routes.ZAPPER].includes(route) &&
    (media.tracks.audio.length > 1 || media.tracks.subtitles.length > 1) &&
    !isCBCSWithAvPlayer
  ) {
    log('getLanguageButton: adding LANGUAGES', {
      audio: media.tracks.audio.length,
      subtitles: media.tracks.subtitles.length,
    });
    buttons.push(Buttons.LANGUAGES);
  } else {
    log('getLanguageButton: skipped', {
      route,
      audio: media.tracks.audio.length,
      subtitles: media.tracks.subtitles.length,
    });
  }
}

/**
 * Adds last 7 days (L7D) button if feature is enabled and channel supports it.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getL7DButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.LAST_SHOWS, [
    ...(asset?.channel?.extraPlans.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  if (
    planToEvaluate &&
    asset &&
    !asset.channel?.isFast &&
    asset.channel?.id &&
    (asset.channel.services.includes(Services.YOU_MISSED_IT) ||
      asset.channel.services.includes(Services.YOU_MISSED_IT_FREE)) &&
    router.route.Page !== Pages.LAST_SHOWS_CHANNEL
  ) {
    log('getL7DButton: adding LAST7DAYS');
    buttons.push(Buttons.LAST7DAYS);
  } else {
    log('getL7DButton: skipped', {
      hasLastShowsPlan: !!planToEvaluate,
      isFast: asset?.channel?.isFast,
      channelServices: asset?.channel?.services,
      page: router.route.Page,
    });
  }
}

/**
 * Adds channel guide button if event has date information.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getChannelGuideButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (asset?.date?.start && asset?.date?.end && asset?.channel) {
    const {
      router: {
        route: { Page },
      },
    } = store.getState();
    if (Page !== Pages.CHANNEL_GUIDE && Page !== Pages.PLAYER) {
      log('getChannelGuideButton: adding GUIDE');
      buttons.push(Buttons.GUIDE);
    } else {
      log('getChannelGuideButton: skipped, page', Page);
    }
  } else {
    log('getChannelGuideButton: skipped, asset without dates or channel');
  }
}

/**
 * Adds watch everything button for FAST channels.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getWatchEverythingButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (
    asset?.channel?.isFast &&
    asset.channel.type !== Content.AVOD &&
    asset.channel.type !== Content.SVOD
  ) {
    const { router } = store.getState();
    if (router.route.Page !== Pages.WATCH_EVERYTHING) {
      log('getWatchEverythingButton: adding WATCH_EVERYTHING');
      buttons.push(Buttons.WATCH_EVERYTHING);
    } else {
      log('getWatchEverythingButton: skipped, already on watch everything');
    }
  } else {
    log('getWatchEverythingButton: skipped, not a FAST linear channel');
  }
}

/**
 * Adds watch later/keep watching button based on user state and content type.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getWatchLaterButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { keepWatching, router, user } = store.getState();
  if (router.route.Page === Pages.PLAYER || router.route.Page === Pages.ZAPPER)
    return;
  if (
    asset &&
    (asset?.type === Content.MEDIA_PRODUCTION ||
      asset?.type === Content.SHOW) &&
    (asset as MediaProduction).contentContainerType !== ContainerType.PARENT
  ) {
    if (user.isAnonymous) {
      log('getWatchLaterButton: adding WATCH_LATER_ANONYMOUS');
      buttons.push(Buttons.WATCH_LATER_ANONYMOUS);
    } else {
      const isKeepWatchingEvent = keepWatching.data.find((item) => {
        return item.id === asset.eventId;
      });
      if (isKeepWatchingEvent) {
        log('getWatchLaterButton: adding STOP_WATCHING');
        buttons.push(Buttons.STOP_WATCHING);
      } else {
        const upgradablePlan = ChannelUtils.getUpgradablePlan(asset.channel);
        if (upgradablePlan) {
          log('getWatchLaterButton: adding WATCH_LATER_UPGRADABLE_PLAN');
          buttons.push(Buttons.WATCH_LATER_UPGRADABLE_PLAN);
        } else {
          log('getWatchLaterButton: adding WATCH_LATER');
          buttons.push(Buttons.WATCH_LATER);
        }
      }
    }
  } else {
    log('getWatchLaterButton: skipped, not a watchable content');
  }
}

const Coordinates = {
  CHANNELS_LINEUP: { x: 884, y: 168 },
  DETAIL: { x: 400, y: 386 },
  EPG: { x: 720, y: 428 },
  MOSAIC: { x: 490, y: 256 },
  MOSAIC_RIGHT_BORDER: { x: 1546, y: 256 },
  PAUSE_ADS: { x: 370, y: 330 },
  PHOTO: { x: 490, y: 280 },
  POSTER: { x: 399.5, y: 221.67 },
  RELATED: { x: 300, y: 230 },
  SQUARE: { x: 250, y: 360 },
  ZAPPER: { x: 280, y: 310 },
};

/**
 * Utility object containing helper functions for floating menu operations.
 */
const Utils = {
  /**
   * Generates the list of floating menu buttons based on the current route and asset.
   *
   * @param {Event | null | undefined} asset - Current event asset
   * @param {number} route - Current route identifier
   * @returns {FloatingMenuButton[]} Array of configured menu buttons
   */
  getMenuButtons(asset: Event | null | undefined, route: number) {
    log('getMenuButtons: start', { route, asset: describeAsset(asset) });
    const buttons: FloatingMenuButton[] = [];
    getClassicViewButton(route, asset, buttons);
    getLineupViewButton(route, asset, buttons);
    getGridViewButton(route, asset, buttons);
    getMainButton(route, asset, buttons);
    getRestartButton(route, asset, buttons);
    getRecordButton(route, asset, buttons);
    getDetailButton(route, asset, buttons);
    getL7DButton(route, asset, buttons);
    getChannelGuideButton(route, asset, buttons);
    getWatchEverythingButton(route, asset, buttons);
    getWatchLaterButton(route, asset, buttons);
    getLanguageButton(route, asset, buttons);
    log(
      'getMenuButtons: result',
      buttons.map((button) => button.text)
    );
    return buttons;
  },
};

export { Coordinates, Utils as FloatingMenuUtils };
